import { get } from 'common/src/vtj/browser/http'
import { observable, runInAction } from 'mobx'
import { pollUntilTrue } from 'todistukset-common/src/vtj/poll.util'

type CancelPollingFn = () => void

export interface DocumentDownloadStatusStore {
  documentDownloadStatuses: Map<number, DocumentDownloadStatus>
  pollDocumentDownloadStatus: (
    todistusPyyntoId: number,
    pollSettings?: PollSettings
  ) => CancelPollingFn
}

export interface DocumentDownloadStatus {
  todistusPyyntoId: number
  isDownloadable: boolean
  isPolling: boolean
  pollingTimedOut: boolean
}

export interface PollSettings {
  maxIter?: number
  maxTotalTimeoutMs?: number
  timeoutCurve?: number
}

export const createDocumentDownloadStatusStore =
  (): DocumentDownloadStatusStore => {
    const documentDownloadStatusStore: DocumentDownloadStatusStore = observable(
      {
        documentDownloadStatuses: new Map(),
        pollDocumentDownloadStatus: (
          todistusPyyntoId: number,
          {
            maxIter = 50,
            maxTotalTimeoutMs = 5 * 60 * 1000,
            timeoutCurve = 0.2,
          }: PollSettings = {}
        ) => {
          let shouldStopPolling = false
          const cancelPolling = () => (shouldStopPolling = true)

          const documentStatus =
            documentDownloadStatusStore.documentDownloadStatuses.get(
              todistusPyyntoId
            )

          if (documentStatus?.isPolling || documentStatus?.isDownloadable) {
            return cancelPolling
          }

          documentDownloadStatusStore.documentDownloadStatuses.set(
            todistusPyyntoId,
            {
              todistusPyyntoId,
              isDownloadable: false,
              isPolling: true,
              pollingTimedOut: false,
            }
          )
          void pollUntilTrue(
            async () => {
              if (shouldStopPolling) {
                return true
              }

              const [err, response] = await get<{ isDownloadable: boolean }>(
                `/api/restricted/todistukset/document-download-status/${todistusPyyntoId}`,
                { timeout: 10 * 1000 }
              )

              if (err) {
                return false
              }

              if (response) {
                const isDownloadable = response.data.isDownloadable ?? false

                if (isDownloadable) {
                  return true
                }
              }

              return false
            },
            maxIter,
            maxTotalTimeoutMs,
            timeoutCurve
          )
            // eslint-disable-next-line promise/prefer-await-to-then
            .then((pollingSuccessful) => {
              if (!pollingSuccessful) {
                console.error('Polling timed out')
              }
              runInAction(() => {
                documentDownloadStatusStore.documentDownloadStatuses.set(
                  todistusPyyntoId,
                  {
                    todistusPyyntoId,
                    isDownloadable: pollingSuccessful && !shouldStopPolling,
                    isPolling: false,
                    pollingTimedOut: !pollingSuccessful,
                  }
                )
              })
            })

          return cancelPolling
        },
      }
    )

    return documentDownloadStatusStore
  }
